import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Clipboard,
  RefreshCw,
  Search,
  User,
  X,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Pagination from '../../components/common/Pagination';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import DateRangePicker from '../../components/analytics/DateRangePicker';
import EventDetails from '../../components/analytics/EventDetails';
import { usePanelEvents, usePanelEventNames } from '../../lib/api';
import {
  PRESET_RANGES,
  rangeFromQuery,
} from '../../lib/dateRange';
import { DateRange, PanelEvent, PanelEventSource } from '../../lib/panelTypes';
import { clampPageAfterResponse } from '../../lib/pagination';
import { countryName } from '../../lib/country';

const num = (n: number) => (n || 0).toLocaleString();
const LIMIT = 50;

const presetKeyByLabel: Record<string, string> = {
  Today: 'today',
  Yesterday: 'yesterday',
  'Last 7 days': '7d',
  'Last 30 days': '30d',
  'Last 90 days': '90d',
};

const initialRange = (params: URLSearchParams): DateRange => {
  const preset = params.get('preset');
  if (preset && PRESET_RANGES[preset]) return PRESET_RANGES[preset]();
  return rangeFromQuery(params.get('from'), params.get('to'), 'Custom');
};

const initialPage = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const initialSource = (value: string | null): PanelEventSource =>
  value === 'website' || value === 'both' ? value : 'app';

const sourceOptions: Array<{ value: PanelEventSource; label: string }> = [
  { value: 'app', label: 'App' },
  { value: 'website', label: 'Website' },
  { value: 'both', label: 'Both' },
];

const relativeTime = (iso: string): string => {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const exactTime = (iso: string): string =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(iso));

const displayPlatform = (platform: string | null): string => {
  if (!platform) return '—';
  if (platform.toLowerCase() === 'ios') return 'iOS';
  if (platform.toLowerCase() === 'android') return 'Android';
  if (platform.toLowerCase() === 'web') return 'Web';
  return platform;
};

// The IP's country, plus the phone's region when it differs: a tourist on
// hotel Wi-Fi has a Danish IP but keeps their home region on the phone.
const CountryCell: React.FC<{ event: PanelEvent }> = ({ event }) => {
  const ip = event.context?.ip_country;
  const phone = event.context?.device_region;
  if (!ip && !phone) return <span className="text-gray-400">—</span>;
  const title = [
    ip && `IP: ${countryName(ip)} (${ip})`,
    phone && `Phone region: ${countryName(phone)} (${phone})`,
  ].filter(Boolean).join('\n');
  return (
    <span title={title}>
      {countryName(ip) || '—'}
      {phone && phone !== ip && (
        <span className="ml-1 text-xs text-gray-400">· phone {phone}</span>
      )}
    </span>
  );
};

const ActivityFeed: React.FC = () => {
  const { user: account } = useAuth();
  // Read-only insights accounts get a stripped stream from the API: no
  // visitor/device IDs and no raw payloads, and per-visitor journeys are
  // refused — so no visitor column, filter or journey panel here.
  const isInsights = account?.role === 'insights';
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<DateRange>(() => initialRange(searchParams));
  const [page, setPage] = useState(() => initialPage(searchParams.get('page')));
  const [eventName, setEventName] = useState(searchParams.get('event') || '');
  const [eventInput, setEventInput] = useState(searchParams.get('event') || '');
  const [user, setUser] = useState(isInsights ? '' : searchParams.get('user') || '');
  const [userInput, setUserInput] = useState(isInsights ? '' : searchParams.get('user') || '');
  const [source, setSource] = useState<PanelEventSource>(() => initialSource(searchParams.get('source')));
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, isValidating, error, mutate } = usePanelEvents({
    from: range.from,
    to: range.to,
    event_name: eventName || undefined,
    user: user || undefined,
    source,
    page,
    limit: LIMIT,
  });
  const { names } = usePanelEventNames(range, source);

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const events = useMemo(() => data?.events ?? [], [data?.events]);
  const eventSuggestions = useMemo(
    () => Array.from(new Set([...names, ...events.map((event) => event.event_name)])).sort(),
    [events, names]
  );
  const latestVisitorEvent = user ? events[0] : undefined;
  const columnCount = isInsights ? 6 : 7;

  useEffect(() => {
    const next = new URLSearchParams();
    const preset = presetKeyByLabel[range.label];
    if (preset) {
      next.set('preset', preset);
    } else {
      next.set('from', range.startDate);
      next.set('to', range.endDate);
    }
    if (eventName) next.set('event', eventName);
    if (user) next.set('user', user);
    if (source !== 'app') next.set('source', source);
    if (page > 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [eventName, page, range, setSearchParams, source, user]);

  useEffect(() => {
    const correctedPage = clampPageAfterResponse(page, pages, data?.page);
    if (correctedPage !== page) setPage(correctedPage);
  }, [data?.page, page, pages]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const preset = presetKeyByLabel[range.label];
      if (preset && preset !== 'yesterday') {
        setRange(PRESET_RANGES[preset]());
      } else {
        mutate();
      }
    };

    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible);
  }, [mutate, range.label]);

  const resetAnd = (fn: () => void) => {
    fn();
    setPage(1);
    setExpandedId(null);
  };

  const applyEvent = () => resetAnd(() => setEventName(eventInput.trim()));
  const applyUser = () => resetAnd(() => setUser(userInput.trim()));
  const filterToUser = (visitor: string) => resetAnd(() => {
    setUser(visitor);
    setUserInput(visitor);
  });
  const clearUser = () => resetAnd(() => {
    setUser('');
    setUserInput('');
  });
  const refresh = () => {
    const preset = presetKeyByLabel[range.label];
    if (preset && preset !== 'yesterday') {
      setRange(PRESET_RANGES[preset]());
    } else {
      mutate();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="mb-1 flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Activity className="h-6 w-6 text-indigo-600" /> Events Explorer
          </h2>
          <p className="text-gray-600">
            {num(total)} events · {range.label.toLowerCase()}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={isValidating}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <DateRangePicker value={range} onChange={(nextRange) => resetAnd(() => setRange(nextRange))} />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-white p-4">
        <div>
          <span className="mb-1 block text-xs text-gray-500">Source</span>
          <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5" role="group" aria-label="Event source">
            {sourceOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => resetAnd(() => setSource(option.value))}
                aria-pressed={source === option.value}
                className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                  source === option.value
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="event-filter" className="mb-1 block text-xs text-gray-500">Event</label>
          <div className="flex gap-1">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
              <input
                id="event-filter"
                list="event-name-options"
                value={eventInput}
                onChange={(event) => setEventInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') applyEvent();
                }}
                placeholder="All events"
                className="min-w-[240px] rounded border border-gray-200 py-1.5 pl-8 pr-2 text-sm"
              />
              <datalist id="event-name-options">
                {eventSuggestions.map((name) => <option key={name} value={name} />)}
              </datalist>
            </div>
            <button type="button" onClick={applyEvent} className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white">
              Apply
            </button>
            {eventName && (
              <button
                type="button"
                onClick={() => resetAnd(() => {
                  setEventName('');
                  setEventInput('');
                })}
                className="rounded px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {!isInsights && (
          <div>
            <label htmlFor="user-filter" className="mb-1 block text-xs text-gray-500">Visitor/device ID</label>
            <div className="flex gap-1">
              <input
                id="user-filter"
                value={userInput}
                onChange={(event) => setUserInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') applyUser();
                }}
                placeholder="Filter by visitor ID"
                className="w-56 rounded border border-gray-200 px-2 py-1.5 font-mono text-sm"
              />
              <button type="button" onClick={applyUser} className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white">
                Apply
              </button>
              {user && (
                <button type="button" onClick={clearUser} className="rounded px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100">
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {user && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-semibold text-indigo-950">
                <User className="h-5 w-5" /> Visitor journey
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="max-w-full break-all font-mono text-xs text-indigo-800">{user}</span>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(user)}
                  className="rounded p-1 text-indigo-500 hover:bg-indigo-100"
                  aria-label="Copy visitor ID"
                >
                  <Clipboard className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <button type="button" onClick={clearUser} className="rounded p-1 text-indigo-500 hover:bg-indigo-100" aria-label="Close visitor journey">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
            <div><div className="text-xs text-indigo-600">Events in range</div><div className="font-semibold text-indigo-950">{num(total)}</div></div>
            <div><div className="text-xs text-indigo-600">Linked account</div><div className="truncate font-medium text-indigo-950">{latestVisitorEvent?.identity?.user_id || 'Not linked'}</div></div>
            <div><div className="text-xs text-indigo-600">Latest city</div><div className="font-medium text-indigo-950">{latestVisitorEvent?.context?.city || 'Not set'}</div></div>
            <div><div className="text-xs text-indigo-600">Latest country</div><div className="font-medium text-indigo-950">{latestVisitorEvent ? <CountryCell event={latestVisitorEvent} /> : 'Not set'}</div></div>
            <div><div className="text-xs text-indigo-600">Latest app/build</div><div className="font-medium text-indigo-950">{[latestVisitorEvent?.context?.app_version, latestVisitorEvent?.context?.build_number].filter(Boolean).join(' · ') || 'Not set'}</div></div>
          </div>
        </div>
      )}

      {isLoading && !data ? (
        <LoadingState message="Loading events..." />
      ) : error ? (
        <ErrorState error={(error as Error).message} onRetry={() => mutate()} />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-gray-500">
                  <th className="px-4 py-2 font-medium">Event</th>
                  <th className="w-[150px] px-4 py-2 font-medium">Time</th>
                  {!isInsights && <th className="w-[190px] px-4 py-2 font-medium">Visitor/device</th>}
                  <th className="w-[120px] px-4 py-2 font-medium">City</th>
                  <th className="w-[160px] px-4 py-2 font-medium" title="Country of the sender's IP address">Country</th>
                  <th className="w-[110px] px-4 py-2 font-medium">OS</th>
                  <th className="w-[90px] px-4 py-2 font-medium">Build</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event: PanelEvent) => {
                  const eventKey = event.id || `${event.ts}-${event.event_name}-${event.user ?? ''}`;
                  const expanded = expandedId === eventKey;
                  return (
                    <Fragment key={eventKey}>
                      <tr
                        onClick={() => setExpandedId(expanded ? null : eventKey)}
                        className={`cursor-pointer border-b hover:bg-gray-50 ${expanded ? 'bg-indigo-50/40' : ''}`}
                        aria-expanded={expanded}
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-900">
                          <span className="flex items-center gap-2">
                            {expanded ? <ChevronDown className="h-4 w-4 text-indigo-500" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                            {event.event_name}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-gray-500" title={exactTime(event.ts)}>
                          {relativeTime(event.ts)}
                        </td>
                        {!isInsights && (
                          <td className="px-4 py-2.5">
                            {event.user ? (
                              <button
                                type="button"
                                onClick={(clickEvent) => {
                                  clickEvent.stopPropagation();
                                  filterToUser(event.user ?? '');
                                }}
                                className="max-w-[170px] truncate font-mono text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                                title={`View journey for ${event.user}`}
                              >
                                {event.user}
                              </button>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                        )}
                        <td className="px-4 py-2.5 text-gray-600">{event.context?.city || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600"><CountryCell event={event} /></td>
                        <td className="px-4 py-2.5 text-gray-600" title={event.context?.device_info || undefined}>
                          {displayPlatform(event.context?.platform || null)}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-600" title={event.context?.app_version ? `App ${event.context.app_version}` : undefined}>
                          {event.context?.build_number || '—'}
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={columnCount} className="p-0">
                            <EventDetails
                              event={event}
                              onFilterUser={isInsights ? undefined : filterToUser}
                              onClose={() => setExpandedId(null)}
                              limited={isInsights}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={columnCount} className="px-4 py-10 text-center text-gray-400">
                      No events match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        IP geolocation by{' '}
        <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">DB-IP</a>
      </p>

      <Pagination
        pagination={{ page, pages, total, hasNext: page < pages, hasPrev: page > 1 }}
        disabled={isValidating}
        onPageChange={(nextPage) => {
          setPage(nextPage);
          setExpandedId(null);
        }}
      />
    </div>
  );
};

export default ActivityFeed;
